import {
  theDomWatcher,
  handleNewsletterSubscribed,
  trackNewsletterSignup,
  initProductDescriptions,
} from "@theme/helpers";

const featureLoaders = [
  {
    label: "cart",
    selectors: ["[data-cart-drawer]", "[data-cart-container]", "[data-open-cart]"],
    observe: true,
    loader: () => import("@theme/cart"),
  },
  {
    label: "toasty",
    selectors: ["body"],
    loader: () => import("@theme/toasty"),
  },
  {
    label: "swym-cart-bridge",
    selectors: ["body"],
    loader: () => import("@theme/swym-cart-bridge"),
  },
  {
    label: "bloomreach-cart",
    selectors: ["body"],
    loader: () => import("@theme/bloomreach-cart"),
  },
  {
    label: "gwp",
    // [data-gwp-config] is rendered on every page (layout/theme.liquid) when GWP is enabled, so gwp.js
    // loads on page load and reconciles the cart immediately — no dependence on the drawer hydrating.
    // .side-cart-item--gwp is a belt-and-braces trigger for a leftover gift when GWP is toggled off.
    selectors: ["[data-gwp-config]", ".side-cart-item--gwp"],
    observe: true,
    loader: () => import("@theme/gwp"),
  },
  {
    label: "product-quick-add",
    selectors: ["[quick-add-qf]", "product-form"],
    observe: true,
    loader: () =>
      import("@theme/product-quick-add").then(({ ProductQuickAdd }) => new ProductQuickAdd()),
  },
  {
    label: "main-product",
    selectors: ["main-product"],
    observe: true,
    loader: () => import("@theme/main-product"),
  },
  {
    label: "product-gallery-grid",
    selectors: ['[gallery-container][data-gallery-style="grid"]'],
    loader: () => import("@theme/product-gallery"),
  },
  // {
  //   label: "product-form",
  //   selectors: ["product-form"],
  //   observe: true,
  //   loader: () => import("@theme/option-selectors").then(({ ProductForm }) => new ProductForm()),
  // },
  {
    label: "product-card",
    selectors: ["product-card"],
    loader: () => import("@theme/product-card").then(({ ProductCard }) => new ProductCard()),
  },
  {
    label: "product-card-swatch-swap",
    selectors: ["[data-card-swatch-link]"],
    observe: true,
    loader: () => import("@theme/product-card-swatch-swap"),
  },
  {
    label: "custom-option-dropdown",
    selectors: ["custom-option-dropdown"],
    observe: true,
    loader: () => import("@theme/custom-option-dropdown"),
  },
  {
    label: "delivery-countdown",
    selectors: ["[data-delivery-timer]"],
    observe: true,
    loader: () => import("@theme/delivery-countdown"),
  },
  {
    label: "promo-marquee",
    selectors: ["[data-promo-marquee]"],
    observe: true,
    loader: () =>
      import("@theme/promo-marquee").then(({ initPromoMarquee }) => initPromoMarquee(document)),
  },
  {
    label: "search-pages-articles",
    selectors: ["[data-search-pa]"],
    observe: true,
    loader: () =>
      import("@theme/search-pages-articles").then(({ initSearchPagesArticles }) =>
        initSearchPagesArticles(document),
      ),
  },
  {
    label: "gorgias-chat",
    selectors: ["[data-gorgias-chat]"],
    observe: true,
    loader: () =>
      import("@theme/gorgias-chat").then(({ initGorgiasChat }) => initGorgiasChat()),
  },
  {
    label: "magic360-arrows",
    selectors: ["[data-magic360-spin]"],
    observe: true,
    loader: () => import("@theme/magic360-arrows"),
  },
  {
    label: "magic360-lightbox",
    selectors: ["[data-m360-deferred]"],
    observe: true,
    loader: () => import("@theme/magic360-lightbox"),
  },
  {
    label: "usp-popup",
    selectors: ["[data-usp-popup]"],
    observe: true,
    loader: () => import("@theme/usp-popup"),
  },
  {
    label: "menu-underline",
    selectors: ["[data-menu-underline]"],
    observe: true,
    loader: () => import("@theme/menu-underline"),
  },
  {
    label: "price-increase-bar",
    selectors: ["[data-price-increase-bar]"],
    observe: true,
    loader: () => import("@theme/price-increase-bar"),
  },
  {
    label: "predictive-search",
    selectors: ["predictive-search"],
    loader: () => import("@theme/search").then(({ SearchController }) => new SearchController()),
  },
  {
    label: "menu",
    selectors: ["#open-mobile-menu", ".mobile-menu-outer-wrapper"],
    loader: () => import("@theme/menu").then(({ MenuController }) => new MenuController()),
  },
  {
    label: "accordion",
    selectors: [".accordion"],
    loader: () =>
      import("@theme/accordion").then(({ AccordionController }) => new AccordionController()),
  },
  {
    label: "product-quantity",
    selectors: ["[quantity-select]"],
    observe: true,
    loader: () =>
      import("@theme/product-quantity").then(({ ProductQuantity }) => new ProductQuantity()),
  },
  {
    label: "header-layout",
    selectors: ["[data-header-layout]"],
    loader: () =>
      import("@theme/header-layout").then(
        ({ HeaderLayoutController }) => new HeaderLayoutController(),
      ),
  },
  {
    label: "header-scroll",
    selectors: [".header-outer-shopify-wrapper"],
    loader: () =>
      import("@theme/header-scroll").then(
        ({ HeaderScrollController }) => new HeaderScrollController(),
      ),
  },
  {
    label: "modal-controller",
    selectors: ["[data-modal-trigger]", "[data-modal]"],
    loader: () =>
      import("@theme/modal-controller").then(({ ModalController }) => {
        if (window.__quickfireModalController) {
          return window.__quickfireModalController;
        }
        const instance = new ModalController();
        window.__quickfireModalController = instance;
        return instance;
      }),
  },
  {
    label: "lightbox",
    selectors: ["[data-lightbox-modal]"],
    observe: true,
    loader: () =>
      import("@theme/lightbox").then(({ bootstrapLightbox }) => bootstrapLightbox()),
  },
  {
    label: "announcement-bar",
    selectors: ["[data-announcement-bar]"],
    loader: () =>
      import("@theme/announcement-bar").then(({ AnnouncementBar }) => new AnnouncementBar()),
  },
  {
    label: "hero-progress",
    selectors: ["[data-hero-progress]"],
    observe: true,
    loader: () =>
      import("@theme/hero-progress").then(({ initHeroProgress }) => initHeroProgress(document)),
  },
  {
    label: "countdown",
    // Every [data-countdown] on the page, not just the countdown-bar section — the promo bar has
    // one too, and it sits outside any component that would otherwise initialise it. initCountdown
    // guards on data-countdown-init, so elements the announcement bar already started are skipped.
    selectors: ["[data-countdown]"],
    observe: true,
    loader: () =>
      import("@theme/countdown").then(({ initCountdowns }) => initCountdowns(document)),
  },
  {
    label: "usp-slider",
    selectors: ["[data-usp-slider]"],
    observe: true,
    loader: () =>
      import("@theme/usp-slider").then(({ initUspSliders }) => initUspSliders(document)),
  },
  {
    label: "accordion-media",
    selectors: ["[data-accordion-media]"],
    observe: true,
    loader: () =>
      import("@theme/accordion-media").then(({ initAccordionMedia }) =>
        initAccordionMedia(document),
      ),
  },
  {
    label: "slider-peek",
    selectors: ["[data-slider-peek]"],
    observe: true,
    loader: () =>
      import("@theme/slider-peek").then(({ initSliderPeek }) => initSliderPeek(document)),
  },
  {
    label: "filter-visibility",
    selectors: ["[data-filter-visibility-toggle]"],
    observe: true,
    loader: () =>
      import("@theme/filter-visibility").then(({ initFilterVisibility }) =>
        initFilterVisibility(document),
      ),
  },
  {
    label: "filter-drawer",
    selectors: [".filters-drawer-mode"],
    observe: true,
    loader: () =>
      import("@theme/filter-drawer").then(({ initFilterDrawer }) => initFilterDrawer(document)),
  },
  {
    label: "cart-reserve-timer",
    selectors: ["[data-cart-reserve-timer]"],
    // observe: the side cart is injected/re-rendered after load, so the hook can appear late.
    observe: true,
    loader: () => import("@theme/cart-reserve-timer"),
  },
  {
    label: "collection-toolbar",
    selectors: ["collection-toolbar"],
    observe: true,
    loader: () => import("@theme/collection-toolbar"),
  },
  {
    label: "search-metal-filter",
    selectors: ["search-metal-filter"],
    observe: true,
    loader: () => import("@theme/search-metal-filter"),
  },
  {
    label: "cart-upsell",
    selectors: ["[data-cart-upsell]"],
    observe: true,
    loader: () => import("@theme/cart-upsell").then(({ CartUpsell }) => new CartUpsell()),
  },
  {
    label: "price-bands",
    selectors: ["[data-price-bands]"],
    observe: true,
    loader: () =>
      import("@theme/price-bands").then(({ initPriceBands }) => initPriceBands(document)),
  },
  {
    label: "price-band-slider",
    selectors: ["price-band-slider"],
    observe: true,
    loader: () =>
      import("@theme/price-band-slider").then(({ initPriceBandSliders }) => initPriceBandSliders(document)),
  },
  {
    label: "filter-search",
    selectors: ["[data-filter-search]"],
    observe: true,
    loader: () =>
      import("@theme/filter-search").then(({ initFilterSearch }) => initFilterSearch(document)),
  },
  {
    label: "product-tabs",
    selectors: ["[data-product-tabs-section]"],
    observe: true,
    loader: () =>
      import("@theme/product-tabs").then(({ initProductTabs }) => initProductTabs(document)),
  },
  {
    label: "cta-product-grid",
    selectors: ["[data-cpg-soften-scroll]"],
    observe: true,
    loader: () =>
      import("@theme/cta-product-grid").then(({ initCtaProductGrid }) =>
        initCtaProductGrid(document),
      ),
  },
  {
    label: "blog-toolbar",
    selectors: ["[data-blog-listing]"],
    observe: true,
    loader: () =>
      import("@theme/blog-toolbar").then(({ initBlogToolbar }) => initBlogToolbar(document)),
  },
  {
    label: "anchor-nav",
    selectors: ["[data-anchor-nav]"],
    observe: true,
    loader: () => import("@theme/anchor-nav").then(({ initAnchorNav }) => initAnchorNav(document)),
  },
  {
    label: "product-range-selector",
    selectors: ["product-range-selector"],
    observe: true,
    loader: () => import("@theme/product-range-selector"),
  },
  {
    label: "carat-slider",
    selectors: ["carat-slider"],
    observe: true,
    loader: () => import("@theme/carat-slider"),
  },
  {
    label: "video-controls",
    selectors: ["[js-video-controls]"],
    observe: true,
    loader: () => import("@theme/video-controls"),
  },
  {
    label: "pill-slider",
    selectors: ["pill-slider"],
    observe: true,
    loader: () => import("@theme/pill-slider"),
  },
  {
    label: "size-gate",
    // Products that must have a size chosen before Add to bag (bracelets). Only does anything when
    // the page was loaded with ?variant= — see assets/size-gate.js.
    selectors: ["main-product[data-size-gate]"],
    loader: () => import("@theme/size-gate"),
  },
  {
    label: "sticky-atc",
    selectors: ["mobile-sticky-atc"],
    observe: true,
    loader: () => import("@theme/sticky-atc"),
  },
  {
    label: "product-personalise",
    selectors: ["product-personalise"],
    observe: true,
    loader: () => import("@theme/product-personalise"),
  },
  {
    label: "size-selector",
    selectors: ["size-selector"],
    observe: true,
    loader: () => import("@theme/size-selector"),
  },
  {
    label: "ring-sizer",
    selectors: ["[data-address-search]"],
    observe: true,
    loader: () => import("@theme/ring-sizer").then(({ initRingSizer }) => initRingSizer(document)),
  },
  {
    label: "yotpo-loyalty",
    selectors: ["[data-yotpo-balance]", "[data-yotpo-earn]"],
    observe: true,
    loader: () =>
      import("@theme/yotpo-loyalty").then(({ initYotpoLoyalty }) => initYotpoLoyalty()),
  },
];

featureLoaders.forEach(({ selectors, loader, label, observe }) =>
  theDomWatcher(selectors, loader, { label, observe }),
);

theDomWatcher(
  "[data-blaze-slider]",
  () =>
    import("@theme/blaze-slider-controller").then(({ blazeSliderController }) => {
      blazeSliderController.init(document);
      return blazeSliderController;
    }),
  { label: "blaze-slider", observe: true },
);

theDomWatcher("form.newsletter-form", handleNewsletterSubscribed, { label: "newsletter-form" });
theDomWatcher('input[name="contact[email]"]', trackNewsletterSignup, {
  label: "newsletter-signup",
});
theDomWatcher("[read-more-container]", initProductDescriptions, { label: "read-more" });
